/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { FC, useMemo, useState } from 'react';
import { Button, Tag, Tooltip, message, Switch } from 'antd';
import {
  BankOutlined,
  CrownOutlined,
  DownloadOutlined,
  DownOutlined,
  MailOutlined,
  RightOutlined,
  TrophyOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useIntl } from 'react-intl';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { OffersResultsExpanded } from './OffersResultsExpanded';
import {
  buildMergedEvaluationVouchers,
  EvaluationValue,
  EvaluationVoucherLike,
  EvaluationVoucherResponseLike,
  getEvaluationOption,
  getOfferVendorCompany,
  getOfferVendorEmail,
  getOfferVendorName,
  ResultOfferLike,
} from './resultsData';
import { downloadOfferZip } from '~src/utils/downloader/downloadOfferZip';

type OffersResultsRowProps = {
  projectId: number;
  tenderId: number;
  contractId: number;
  contractName?: string;
  offer: ResultOfferLike;
  serial: string;
  vouchers: EvaluationVoucherLike[];
  responses?: EvaluationVoucherResponseLike[];
  resultValue: EvaluationValue;
  isWinner?: boolean;
  onSetWinner?: () => void;
  onRemoveWinner?: () => void;
};

export const OffersResultsRow: FC<OffersResultsRowProps> = ({
  projectId,
  tenderId,
  contractId,
  contractName,
  offer,
  serial,
  vouchers,
  responses = [],
  resultValue,
  isWinner = false,
  onSetWinner,
  onRemoveWinner,
}) => {
  const styles = useStyles(styleFn);
  const intl = useIntl();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isZipDownloading, setIsZipDownloading] = useState(false);

  const mergedVouchers = useMemo(
    () => buildMergedEvaluationVouchers(vouchers, responses, intl),
    [vouchers, responses, intl]
  );

  const vendorName = getOfferVendorName(offer, intl);
  const vendorCompany = getOfferVendorCompany(offer);
  const vendorEmail = getOfferVendorEmail(offer);

  const handleZipDownload = async () => {
    setIsZipDownloading(true);

    // Activate the download progress modal
    const { useStore } = await import('~src/store');
    const { DownloadingState } = await import('~src/store');
    useStore.getState().setIsOfferDownload(true);
    useStore.getState().changeDownloadingActive(true);
    useStore.getState().changeDownloadingState(DownloadingState.PREPARING);

    try {
      await downloadOfferZip({
        projectId,
        tenderId,
        contractId,
        offerId: offer.id,
        contractName: contractName || `Contract_${contractId}`,
        offerName: `${serial} ${vendorName}`,
        mergedVouchers,
        offerSerial: serial,
      });
    } catch {
      message.error(intl.formatMessage({ id: 'attachment-download-failed' }));
    } finally {
      setIsZipDownloading(false);
      // Modal will auto-close after FINISHING state (handled by saveFilesToZip)
    }
  };

  const selectedOption = getEvaluationOption(resultValue);
  const isPriority = resultValue === 5;

  return (
    <div css={styles.wrapper}>
      <div css={styles.row}>
        <button
          type="button"
          css={styles.expandButton}
          onClick={() => setIsExpanded((prev) => !prev)}>
          {isExpanded ? <DownOutlined /> : <RightOutlined />}
        </button>

        <div css={styles.vendorCell}>
          <div css={styles.vendorTitleRow}>
            <UserOutlined css={styles.vendorIcon} />
            <span css={styles.vendorName}>
              {vendorName} <span css={styles.offerSerial}>{serial}</span>
            </span>
            {isWinner && (
              <Tooltip
                title={intl.formatMessage({
                  id: 'offers-results-winner-badge',
                })}>
                <TrophyOutlined css={styles.winnerBadgeIcon} />
              </Tooltip>
            )}
          </div>

          <div css={styles.vendorMetaRow}>
            <span css={styles.metaItem}>
              <BankOutlined />
              <span>{vendorCompany}</span>
            </span>

            <span css={styles.metaItem}>
              <MailOutlined />
              <span>{vendorEmail}</span>
            </span>
          </div>
        </div>

        <div css={styles.statusCell}>
          <div css={styles.cellLabel}>
            {intl.formatMessage({
              id: 'results-row-status-label',
            })}
          </div>

          <Tag
            css={[
              styles.statusTag,
              {
                color: selectedOption.color,
                borderColor: selectedOption.color,
                background: selectedOption.background,
              },
            ]}>
            {isPriority && <CrownOutlined css={styles.priorityIcon} />}
            {intl.formatMessage({
              id: selectedOption.intlId,
            })}
          </Tag>
        </div>

        <div css={styles.winnerCell}>
          <div css={styles.cellLabel}>
            {intl.formatMessage({ id: 'offers-results-winner-label' })}
          </div>

          <div css={styles.winnerToggleWrapper}>
            <Tooltip
              title={intl.formatMessage({
                id: isWinner
                  ? 'offers-results-remove-winner-tooltip'
                  : 'offers-results-set-winner-tooltip',
              })}>
              <Switch
                checked={isWinner}
                onChange={(checked) => {
                  if (checked) {
                    onSetWinner?.();
                  } else {
                    onRemoveWinner?.();
                  }
                }}
                checkedChildren={<TrophyOutlined />}
                unCheckedChildren={<TrophyOutlined />}
                css={styles.winnerToggle}
              />
            </Tooltip>
          </div>
        </div>

        <div css={styles.actionCell}>
          <Button
            size="small"
            css={styles.detailsButton}
            onClick={() => setIsExpanded((prev) => !prev)}>
            {isExpanded
              ? intl.formatMessage({
                  id: 'evaluation-row-hide-details',
                })
              : intl.formatMessage({
                  id: 'evaluation-row-view-details',
                })}
          </Button>

          <Tooltip
            title={intl.formatMessage({ id: 'evaluation-row-download-zip' })}>
            <Button
              size="small"
              icon={<DownloadOutlined />}
              loading={isZipDownloading}
              onClick={handleZipDownload}
              css={styles.zipButton}
              aria-label={intl.formatMessage({
                id: 'evaluation-row-download-all',
              })}
            />
          </Tooltip>
        </div>
      </div>

      {isExpanded && (
        <div css={styles.expandedContent}>
          <OffersResultsExpanded
            projectId={projectId}
            tenderId={tenderId}
            contractId={contractId}
            offerId={offer.id}
            vouchers={mergedVouchers}
          />
        </div>
      )}
    </div>
  );
};

const styleFn: ThemedStyleFn = () => ({
  wrapper: {
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    border: '1px solid #e8e8e8',
    borderRadius: 6,
    background: '#ffffff',
    overflow: 'hidden',
    boxSizing: 'border-box',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)',
  },

  row: {
    display: 'grid',
    gridTemplateColumns:
      '24px minmax(300px, 1.45fr) minmax(160px, 0.75fr) 120px 180px',
    alignItems: 'center',
    gap: 16,
    minHeight: 86,
    padding: '14px 16px',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    boxSizing: 'border-box',

    '&:hover': {
      background: '#fafafa',
    },
  },

  expandButton: {
    width: 24,
    height: 24,
    padding: 0,
    border: 'none',
    background: 'transparent',
    color: '#595959',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,

    '&:hover': {
      color: '#1890ff',
    },
  },

  vendorCell: {
    minWidth: 0,
    overflow: 'hidden',
  },

  vendorTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    minWidth: 0,
    overflow: 'hidden',
  },

  vendorIcon: {
    color: '#52c41a',
    flexShrink: 0,
  },

  vendorName: {
    fontWeight: 700,
    color: '#222222',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  offerSerial: {
    color: '#8c8c8c',
    fontWeight: 400,
    marginRight: 8,
    fontSize: '10px',
  },

  vendorMetaRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '6px 14px',
    marginTop: 7,
    minWidth: 0,
    maxWidth: '100%',
    overflow: 'hidden',
    color: '#666666',
    fontSize: 12,
  },

  metaItem: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    minWidth: 0,
    overflow: 'hidden',

    span: {
      minWidth: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  },

  statusCell: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    height: 49,
    minWidth: 0,
    width: '100%',
    overflow: 'hidden',
  },

  cellLabel: {
    height: 11,
    color: '#999999',
    fontSize: 11,
    fontWeight: 700,
    lineHeight: '11px',
    marginBottom: 6,
    textTransform: 'uppercase',
    letterSpacing: 0.3,
    whiteSpace: 'nowrap',
  },

  statusTag: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 'fit-content',
    maxWidth: '100%',
    height: 32,
    minHeight: 32,
    margin: 0,
    padding: '0 12px',
    lineHeight: '30px',
    fontWeight: 700,
    boxSizing: 'border-box',

    '.anticon': {
      display: 'inline-flex',
      alignItems: 'center',
    },
  },

  priorityIcon: {
    marginRight: 5,
    fontSize: 12,
  },

  winnerCell: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    height: 49,
    minWidth: 0,
    width: '100%',
    overflow: 'visible',
  },

  actionCell: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'flex-end',
    gap: 8,
    height: 49,
    minWidth: 0,
    width: '100%',
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
  },

  detailsButton: {
    width: 132,
    minWidth: 132,
    height: 32,
    minHeight: 32,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 14px',
    lineHeight: '30px',
    boxSizing: 'border-box',
    boxShadow: 'none',

    '&:hover, &:focus, &:active': {
      height: 32,
      minHeight: 32,
      boxShadow: 'none',
    },

    span: {
      lineHeight: '30px',
    },
  },

  zipButton: {
    width: 32,
    minWidth: 32,
    height: 32,
    minHeight: 32,
    padding: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 4,
    lineHeight: '30px',
    boxSizing: 'border-box',
    boxShadow: 'none',

    '&:hover, &:focus, &:active': {
      width: 32,
      minWidth: 32,
      height: 32,
      minHeight: 32,
      boxShadow: 'none',
    },
  },

  winnerBadgeIcon: {
    color: '#faad14',
    fontSize: 14,
    flexShrink: 0,
    marginLeft: 4,
  },

  winnerToggleWrapper: {
    display: 'flex',
    alignItems: 'center',
    height: 32,
    minHeight: 32,
  },

  winnerToggle: {
    '&.ant-switch': {
      height: 32,
      minHeight: 32,
      minWidth: 60,
      lineHeight: '30px',
    },
    '&.ant-switch .ant-switch-handle': {
      height: 24,
      width: 24,
      top: 3,
      left: 4,
    },
    '&.ant-switch .ant-switch-handle::before': {
      borderRadius: '50%',
    },
    '&.ant-switch-checked .ant-switch-handle': {
      left: 'calc(100% - 28px)',
    },
    '&.ant-switch .ant-switch-inner': {
      marginLeft: 32,
      marginRight: 8,
      height: 32,
      lineHeight: '32px',
      display: 'flex',
      alignItems: 'center',
      fontSize: 12,
    },
    '&.ant-switch-checked .ant-switch-inner': {
      marginLeft: 8,
      marginRight: 32,
    },
    '&.ant-switch-checked': {
      backgroundColor: '#faad14',
    },
  },

  setWinnerButton: {
    height: 32,
    minHeight: 32,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 12px',
    lineHeight: '30px',
    color: '#faad14',
    borderColor: '#ffd666',
    background: '#ffffff',
    boxSizing: 'border-box',
    boxShadow: 'none',
    whiteSpace: 'nowrap',

    '&:hover, &:focus, &:active': {
      height: 32,
      minHeight: 32,
      color: '#d48806',
      borderColor: '#faad14',
      background: '#ffffff',
      boxShadow: 'none',
    },

    '.anticon': {
      display: 'inline-flex',
      alignItems: 'center',
    },

    span: {
      lineHeight: '30px',
    },
  },

  removeWinnerButton: {
    height: 32,
    minHeight: 32,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 12px',
    lineHeight: '30px',
    color: '#ff4d4f',
    borderColor: '#ffadb5',
    background: '#ffffff',
    boxSizing: 'border-box',
    boxShadow: 'none',
    whiteSpace: 'nowrap',

    '&:hover, &:focus, &:active': {
      height: 32,
      minHeight: 32,
      color: '#cf1322',
      borderColor: '#ff4d4f',
      background: '#ffffff',
      boxShadow: 'none',
    },

    span: {
      lineHeight: '30px',
    },
  },

  expandedContent: {
    padding: '0 16px 16px 16px',
    background: '#fcfcfc',
  },
});
